// Library Imports
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// Local Imports
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { validateEmail } from '../../utils/validator';
import assets from '../../assets';
import { Alert, Button, TextField } from '../shared';
import { forgetPasswordAPI } from '../../redux/features/auth/authSlice';

interface ForgetPassFormData {
    email: string;
}

const ForgetPasswordForm = () => {
    const dispatch = useAppDispatch();
    const { loading, error } = useAppSelector((state) => state.authUser);

    // States
    const [forgetPassFormData, setForgetPassFormData] = useState<ForgetPassFormData>({
        email: '',
    });
    const [isShowAlert, setIsShowAlert] = useState<boolean>(false);
    const [isRequestSend, setIsRequestSend] = useState<boolean>(false);
    const [emptyData, setEmptyData] = useState<any>([]);

    // Empty Check Fn
    const emptyCheck = () => {
        let isEmpty: any = {};
        Object.values(forgetPassFormData).map((item, index) => {
            if (item === null || item === '') {
                isEmpty = {
                    ...isEmpty,
                    [Object.keys(forgetPassFormData)[index]]: true,
                };
            }
            return item;
        });
        if (!validateEmail(forgetPassFormData.email) && forgetPassFormData.email !== '') {
            isEmpty = {
                ...isEmpty,
                isEmailValid: true,
            };
        }
        setEmptyData(isEmpty);

        return isEmpty ? (Object.keys(isEmpty).length === 0 ? false : true) : false;
    };

    // Handle FieldChange Fn
    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForgetPassFormData({ ...forgetPassFormData, [name]: value });
        setEmptyData({
            ...emptyData,
            [name]: false,
            ...(name === 'email' ? { isEmailValid: false } : {}),
        });
        setIsShowAlert(false);
    };

    // Handle Forget Password Fn
    const handleForgetPassword = async () => {
        setIsShowAlert(false);
        if (!emptyCheck()) {
            const data = await dispatch(forgetPasswordAPI(forgetPassFormData));
            if (data.meta.requestStatus === 'rejected') {
                setIsShowAlert(true);
                return
            }
            setIsRequestSend(true);
        }
    };

    // UseEffect
    useEffect(() => {
        const listener = (event: KeyboardEvent) => {
            if ((event.code === "Enter" || event.code === "NumpadEnter") && !isRequestSend) {
                event.preventDefault();
                handleForgetPassword();
            }
        };
        document.addEventListener("keydown", listener);
        return () => {
            document.removeEventListener("keydown", listener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forgetPassFormData, isRequestSend]);

    return (
        <React.Fragment>
            <div className='h-full w-full flex flex-col items-center justify-center gap-3'>
                {/* LOGO */}
                <div className='w-[150px]'>
                    <Link to='/'>
                        <img src={assets.logo} className='object-fit' alt='Rylyf' />
                    </Link>
                </div>

                {/* Form Section */}
                <section className='w-full max-w-md rounded-xl'>
                    <div className='container flex items-center justify-center px-6 mx-auto'>
                        <div className='w-full max-w-md'>
                            {/* LOGO Name */}
                            <div className='flex flex-col items-center justify-center'>
                                <h1 className='text-2xl font-normal text-gray-800 capitalize sm:text-2xl'>
                                    {isRequestSend ? 'Request Send Successfully' : 'Forget Password'}
                                </h1>
                                {isRequestSend ?
                                    <p className='text-base text-center text-gray-500 mt-4'>We have sent a confirmation email to {forgetPassFormData?.email}. Please check your email.</p> :
                                    <p className='text-base text-center text-gray-500 mt-1'>Enter your email and we will send you a link to reset your password.</p>
                                }
                            </div>

                            <Alert color='danger' text={error ? error : ''} showAlert={isShowAlert} setShowAlert={setIsShowAlert} />

                            {/* Login Fields */}
                            {!isRequestSend && <div className='relative flex flex-col items-center mt-4 gap-4'>
                                <TextField
                                    name={'email'}
                                    value={forgetPassFormData?.email}
                                    type='email'
                                    placeholder='Email'
                                    icon={true}
                                    onChange={handleOnChange}
                                    error={
                                        emptyData?.email || emptyData?.isEmailValid || isShowAlert ? true : false
                                    }
                                    errorMsg={
                                        (emptyData?.email && 'Email is required') ||
                                        (emptyData?.isEmailValid && 'Email address is not valid')
                                    }
                                />
                            </div>}

                            {/* Button Section */}
                            <div className='mt-4 flex flex-col gap-2'>
                                {!isRequestSend && <Button onClick={handleForgetPassword} variant='primary' fullWidth loading={loading}>
                                    Reset Password
                                </Button>}

                                <Link
                                    to='/login'
                                    className='text-sm no-underline'
                                >
                                    <Button variant={isRequestSend ? 'primary' : 'secondary'} fullWidth disabled={loading}
                                        onClick={() => setIsShowAlert(false)}
                                    >
                                        {isRequestSend ? 'Done' : "Back"}
                                    </Button>
                                </Link>

                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </React.Fragment>
    );
};

export default ForgetPasswordForm;
